import { useTranslation } from "gatsby-plugin-react-i18next";
import { useEffect, type FC, type ReactNode } from "react";
import CartModal from "../../components/general/CartModal";
import { useCartProducts } from "../../hooks/use-cart-products";
import { useCartService } from "../../hooks/use-cart-service";
import { useTranslatedPaths } from "../../hooks/use-translated-paths";

export type emptyCartContent = {
	title: string;
	description: string;
	backgroundImage: ReactNode;
	bannerIcon: string;
	bannerText: string;
	buttonHref: string;
	buttonLabel: string;
	buttonIcon?: string;
};

type CartContainerProps = {
	closeModal: () => void;
	emptyCartData: emptyCartContent;
};

const CartContainer: FC<CartContainerProps> = ({
	closeModal,
	emptyCartData,
}) => {
	const { t } = useTranslation();
	const { cartPath } = useTranslatedPaths();

	const {
		freeShipping,
		products,
		removeFromCart,
		decrementProductQuantity,
		incrementProductQuantity,
		subTotal,
		pending,
	} = useCartProducts();

	const { refreshCart } = useCartService();

	const incrementQuantity = (productId: string) => () =>
		incrementProductQuantity(productId);
	const decrementQuantity = (productId: string) => () =>
		decrementProductQuantity(productId);
	const remove = (productId: string) => async () => {
		await removeFromCart(productId);
	};

	useEffect(() => {
		void refreshCart();
	}, [refreshCart]);

	return (
		<CartModal
			disabled={pending}
			overlayOnClick={closeModal}
			buttonOnClick={closeModal}
			closeButtonLabel={t("Close")}
			heading={t("Cart")}
			emptyCartData={emptyCartData}
			cartItems={products.map((item) => ({
				...item,
				increment: incrementQuantity(item.id),
				decrement: decrementQuantity(item.id),
				remove: remove(item.id),
				quantityLabel: t("Quantity"),
			}))}
			totalAmount={subTotal}
			freeShippingLabel={freeShipping.freeShippingUSP}
			checkoutLink={cartPath}
		/>
	);
};

export default CartContainer;
